import { useEffect, useMemo, useState } from 'react';
import {
  experiment,
  tracking,
  useMultipleExperiments,
} from '@podiumhq/tracking';

import useQueryParams from 'hooks/useQueryParams';
import {
  BUY_NOW_WITH_DISCOUNT,
  ROI_SIGNUP_FLOW,
} from 'lib/amplitude/flags.constants';
import { PROMO_CODES } from 'lib/amplitude/experiments.constants';
import { getCookies } from 'lib/getCookies';

import { SIDEBAR_DEFAULT, SIDEBAR_VARIANTS } from '../constants';
import { isSourceMobile } from '../utils';
import { addErrorToDatadog } from 'lib/datadogError';
import { useTimeout } from '@chakra-ui/hooks';

const LOADING_EXPERIMENTS_TIMEOUT = 2000;

export const useTrialTrackingData = () => {
  const [loadingAllExperiments, allExperiments] = useMultipleExperiments([
    BUY_NOW_WITH_DISCOUNT,
    PROMO_CODES,
    ROI_SIGNUP_FLOW,
  ]);
  const { value: productEntrySlug } = useQueryParams('product');
  const { value: scidFromParams } = useQueryParams('SCID');
  const { value: viewFlow } = useQueryParams('view');
  const { value: utmMediumFromParams } = useQueryParams('utm_medium');
  const { value: source } = useQueryParams('source');
  const { value: discountCode } = useQueryParams('discount');
  const { value: partnerIdFromParams } = useQueryParams('partner_id');

  const [loadingExperiments, setLoadingExperiments] = useState(true);
  const [fbclid, setFbclid] = useState('');
  const [firstUtmMedium, setFirstUtmMedium] = useState(null);
  const [googleClickId, setGoogleClickId] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [instavarValue, setInstavarValue] = useState('');
  const [optvarValue, setOptVarValue] = useState('');
  const [msclkid, setMsclkid] = useState('');
  const [partnerId, setPartnerId] = useState(null);
  const [salesforceCampaignIdFromCookies, setSalesforceCampaignIdFromCookies] =
    useState(null);
  const [ssqt, setSsqt] = useState('');
  const [utmMediumFromCookies, setUtmMediumFromCookies] = useState(null);
  const isMobileAppSignup = isSourceMobile(source);
  const [scid, setScid] = useState('');

  const abExperiments = useMemo(
    () => ({
      promoAmplitudeExperiment: allExperiments[PROMO_CODES]?.value,
    }),
    [allExperiments]
  );

  const flows = useMemo(
    () => ({
      isRoiFlow:
        allExperiments[ROI_SIGNUP_FLOW]?.value === 'on' ||
        viewFlow === 'roi-flow',
      showDiscountPricing:
        allExperiments[BUY_NOW_WITH_DISCOUNT]?.value === 'on' ||
        viewFlow === 'buy-now',
      viewFlow,
    }),
    [allExperiments, viewFlow]
  );

  const trialPageVariantSlug = useMemo(() => {
    const variantSlug = productEntrySlug?.replace(/^\/|\/$/g, '');

    return SIDEBAR_VARIANTS.includes(variantSlug)
      ? variantSlug
      : SIDEBAR_DEFAULT;
  }, [productEntrySlug]);

  const buildScid = (scid) => {
    const now = new Date();
    const timeStamp =
      'T' +
      now.toUTCString().split(' ')[4] +
      now.toDateString().replaceAll(' ', '') +
      '#';
    return timeStamp + scid;
  };

  useTimeout(() => {
    if (loadingExperiments) {
      addErrorToDatadog(`Error loading experiments: stop loading spinner`);
      setLoadingExperiments(false);
    }
  }, LOADING_EXPERIMENTS_TIMEOUT);

  useEffect(() => {
    if (!loadingAllExperiments) {
      setLoadingExperiments(loadingAllExperiments);
    }
  }, [loadingAllExperiments]);

  useEffect(() => {
    if (loadingExperiments) return;

    const allCookies = getCookies();
    const clientId = allCookies['_ga']?.split('.')?.slice(-2)?.join('.');
    const trialVersion = 'v5';

    let userProperties = { 'Checkout Trial Version': trialVersion };

    setFbclid(allCookies['fbclid']);
    setFirstUtmMedium(allCookies['first-utm-medium']);
    setGoogleClickId(allCookies['gclid']);
    setGoogleClientId(clientId);
    setInstavarValue(allCookies['instavar'] || 'none');
    setOptVarValue(allCookies['optvar'] || 'none');
    setMsclkid(allCookies['msclkid']);
    setPartnerId(allCookies['partner-id']);
    setSalesforceCampaignIdFromCookies(allCookies['scid']);
    setSsqt(allCookies['ssqt']);
    setUtmMediumFromCookies(allCookies['utm-medium']);

    if (scidFromParams) {
      const scidTimeStamp = buildScid(scidFromParams);
      setScid(
        (allCookies['scid'] ? allCookies['scid'] + ',' : '') + scidTimeStamp
      );
    }

    if (clientId) {
      userProperties = { ...userProperties, 'Google Client Id': clientId };
    }

    tracking?.setUserProperties(userProperties);

    experiment?.fetch({ free_trial_signup: 'true' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingExperiments]);

  return useMemo(
    () => ({
      ...abExperiments,
      discountCode,
      ...flows,
      fbclid,
      firstUtmMedium,
      googleClickId,
      googleClientId,
      instavarValue,
      loadingExperiments,
      isMobileAppSignup,
      msclkid,
      optvarValue,
      partnerId: partnerIdFromParams || partnerId,
      productEntrySlug,
      salesforceCampaignId: scid || salesforceCampaignIdFromCookies,
      ssqt,
      trialPageVariantSlug,
      utmMedium: utmMediumFromParams || utmMediumFromCookies,
    }),
    [
      abExperiments,
      discountCode,
      fbclid,
      firstUtmMedium,
      flows,
      googleClickId,
      googleClientId,
      instavarValue,
      loadingExperiments,
      isMobileAppSignup,
      msclkid,
      optvarValue,
      partnerId,
      partnerIdFromParams,
      productEntrySlug,
      salesforceCampaignIdFromCookies,
      scid,
      ssqt,
      trialPageVariantSlug,
      utmMediumFromCookies,
      utmMediumFromParams,
    ]
  );
};

export default useTrialTrackingData;
